<template>
  <div style="height: 80vh; width: 100%;">
    <leaflet-map ref="fullMap" style="height: inherit; width: inherit;" fit-always :map-layers="fullMapMapLayers">
      <template #controls-topleft>
        <div class="mt-2 leaflet-control-layers leaflet-control-layers-expanded" v-if="!onlyCheckpoint">
          <h6 v-translate="{ itemsLabel: ecosystemModules.items.label || $gettext('Items') }">%{itemsLabel} in Radius
          </h6>
          <b-form-radio-group stacked v-model="radius" :options="radiusSelect" />
        </div>
      </template>
      <template #controls-topright>
        <div v-for="(order, i) in orders"
          v-if="!onlyCheckpoint && Object.keys(fullMapMapLayers).length > 0 && mapLayers[i]"
          class="mt-2 leaflet-control-layers leaflet-control-layers-expanded">
          <h5 class="mb-0" v-translate="{ orderId: order.id }">Order ID %{orderId}</h5>
          <b-form-checkbox v-model="mapLayers[i].visible">Order Geo Data</b-form-checkbox>
          <h6 class="mb-0">{{ ecosystemModules.items.label || $gettext('Items') }}</h6>
          <div v-if="!onlyCheckpoint">
            <div v-for="checkpoint in ['origin', 'destination']" :key="checkpoint">
              <strong>{{ order[checkpoint].external_id | cleanentityprefix }}</strong> ({{ checkpoint }})<br>
              <small>{{ order[checkpoint].name | cleanentityprefix }} ({{ order[checkpoint].reference_location }})</small>
              <div v-for="status in ['in', 'out']">
                <b-form-checkbox v-model="fullMapMapLayers[order.id + status + order[checkpoint].id].visible" v-if="fullMapMapLayers[order.id + status + order[checkpoint].id]" @change="onLayerToggle(order, checkpoint, status)">{{status}} ({{ fullMapMapLayers[order.id + status + order[checkpoint].id].count }})</b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-2 leaflet-control-layers leaflet-control-layers-expanded" v-else-if="onlyCheckpoint">
          <h6 v-translate="{ itemsLabel: ecosystemModules.items.label || $gettext('Items') }">%{itemsLabel} in Radius
          </h6>
          <b-form-radio-group stacked v-model="radius" :options="radiusSelect" />
          <div v-for="(order, i) in orders"
            v-if="onlyCheckpoint && Object.keys(fullMapMapLayers).length > 0 && mapLayers[i]">
            <hr class="py-0 my-1">
            <h6 class="mb-0">{{ ecosystemModules.items.label || $gettext('Items') }}</h6>
            <div v-for="status in ['in', 'out']">
              <b-form-checkbox v-model="fullMapMapLayers[order.id + status + order[onlyCheckpoint].id].visible" v-if="fullMapMapLayers[order.id + status + order[onlyCheckpoint].id]" @change="onLayerToggle(order, onlyCheckpoint, status)">{{status}} ({{ fullMapMapLayers[order.id + status + order[onlyCheckpoint].id].count }})</b-form-checkbox>
            </div>
          </div>
        </div>
      </template>

      <template #map-data>
        <l-layer-group :ref="'order-' + order.data.id" v-for="(order, i) in mapLayers" :key="'order-' + order.data.id"
          :visible="mapLayers[i].visible || true" v-if="mapLayers[i].visible && order.data?.id">
          <l-geo-json v-if="order.data?.geofences?.length > 0" v-for="(geofence, i) in order.data.geofences"
            :geojson="geofence" :options="options" :options-style="styleFunction"
            :ref="'geofence-' + geofence.properties.id" :key="'geofence-' + geofence.properties.id" />
          <l-geo-json v-if="order.data?.geopaths?.length > 0" v-for="(geopath, i) in order.data.geopaths"
            :geojson="geopath" :options="options" :options-style="styleFunction"
            :ref="'geopath-' + geopath.properties.id" :key="'geopath-' + geopath.properties.id" />
          <l-geo-json v-if="order.data?.geopoints?.length > 0" v-for="(geopoint, i) in order.data.geopoints"
            :geojson="geopoint" :options="options" :options-style="styleFunction"
            :ref="'geopoint-' + geopoint.properties.id" :key="'geopoint-' + geopoint.properties.id" />

          <map-marker v-if="order.data?.couriers?.length > 0" v-for="(marker, i) in order.data.couriers"
            :marker="marker" :key="'courier' + i"></map-marker>

          <map-marker v-if="order.data?.items?.length > 0" v-for="(marker, i) in order.data.items" :marker="marker"
            :key="'item' + i"></map-marker>

        </l-layer-group>
      </template>
    </leaflet-map>

    <b-modal size="lg" :id="'modalItemsFromGeofence' + rnd" :ref="'modalItemsFromGeofence' + rnd" hide-footer
      @hidden="selectedItems = []; selectedGeofence = '';" @show="$parent.$parent.triggers = 'click';"
      @hide="$parent.$parent.triggers = 'hover';">
      <template #modal-title>
        <span v-translate="{itemsLabel: ecosystemModules.items.label || $gettext('Items'), geofence: $options.filters.cleanentityprefix(selectedGeofence.externalId)}">%{itemsLabel} from <strong>%{geofence}</strong></span><br>
        <small>{{ selectedGeofence.name | cleanentityprefix }} ({{selectedGeofence.referenceLocation}})</small>
      </template>
      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
      <b-button size="sm" @click="downloadItemsCsv([...new Set(selectedItems)])" class="mb-3" v-translate>Download CSV</b-button>
      <api-list type="itemsFromGeofence" :model="models.itemsFromGeofence" :staticItems="selectedItems" in-modal
        hide-filters hide-pagination />
    </b-modal>

    <div v-if="updating"
      style="position: absolute; bottom: 5px; left: 50%; transform: translateX(-50%); vertical-align: middle; z-index: 1070;"
      class="bg-info text-white p-1 px-3">
      <small>
        <translate>Updating</translate>&nbsp;<b-spinner small style="vertical-align: middle;" />
      </small>
    </div>

  </div>
</template>

<script>
import {
  GeofenceService,
  ItemService
} from '@/services/api'
import ApiList from '@/components/ApiLists/ApiList'
import { circleMarker } from 'leaflet'
import LeafletMap from '@/components/Maps/LeafletMap'
import {
  LGeoJson,
  LLayerGroup,
  LFeatureGroup
} from 'vue2-leaflet'
import MapMarker from '@/components/Maps/MapMarker'

export default {
  name: 'ShippingOrderMap',
  components: {
    ApiList,
    LeafletMap,
    LGeoJson,
    LLayerGroup,
    LFeatureGroup,
    MapMarker
  },
  props: {
    orders: {
      type: Array,
      default: () => [],
      required: true
    },
    updateEvery: {
      type: [Number, String],
      default: null
    },
    onlyCheckpoint: {
      type: String,
      default: null
    },
    radiusList: {
      type: Array,
      default: () => [5, 10, 15, 20]
    },
    radiusDefault: {
      type: [Number, String],
      default: 10
    }
  },
  data() {
    return {
      markers: [],
      items: {},
      fetchTimeout: null,
      mapLayers: [],
      selectedGeofence: '',
      selectedItems: [],
      selectedItemsCsv: [],
      rnd: Math.random(),
      fullMapMapLayers: {},
      updating: false,
      radius: this.radiusDefault,
      radiusSelect: []
    }
  },
  computed: {
    radiusComputed() {
      return this.radius * this.unitSystemMultiplier
    },
    options() {
      return {
        onEachFeature: this.onEachFeatureFunction,
        pointToLayer: this.pointToLayer
      }
    },
    styleFunction() {
      return (feature) => {
        if (feature.properties.type === 'polygon') {
          return {
            weight: 2,
            color: this.stringToColour('11' + feature.properties.typeName),
            opacity: 1,
            fillColor: this.stringToColour('11' + feature.properties.typeName),
            fillOpacity: 0.5
          }
        } else if (feature.properties.type === 'linestring' && feature.properties.typeName === 'path_entry_loading') {
          return {
            weight: 2,
            color: '#00cc00',
            opacity: 1,
            fillColor: '#00cc00',
            fillOpacity: 1
          }
        } else if (feature.properties.type === 'linestring' && feature.properties.typeName === 'path_loading_exit') {
          return {
            weight: 2,
            color: '#cc0000',
            opacity: 1,
            fillColor: '#cc0000',
            fillOpacity: 1
          }
        } else if (feature.properties.type === 'point' && feature.properties.typeName === 'entry_point') {
          return {
            weight: 1,
            color: '#00cc00',
            opacity: 1,
            fillColor: '#00cc00',
            fillOpacity: 0.75
          }
        } else if (feature.properties.type === 'point' && feature.properties.typeName === 'exit_point') {
          return {
            weight: 1,
            color: '#cc0000',
            opacity: 1,
            fillColor: '#cc0000',
            fillOpacity: 0.75
          }
        }
      }
    },
    onEachFeatureFunction() {
      return (feature, layer) => {
        let tooltip = ''
        if (feature.properties.externalId) {
          tooltip += `<strong>${this.$options.filters.cleanentityprefix(feature.properties.externalId)}</strong> `
        }
        tooltip += this.$options.filters.cleanentityprefix(feature.properties.name)
        if (feature.properties.referenceLocation) {
          tooltip += ` (${feature.properties.referenceLocation})`
        }
        layer.bindTooltip(
          tooltip,
          { permanent: false, sticky: false }
        )
        if (feature.properties.type === 'polygon' && feature.properties.level === 0) {
          // const mssg = `<button title="${this.$gettext('Edit this GeoFence')}" type="button" class="btn btn-dark" onclick="window.open('${this.$router.resolve({ name: 'GeofencesEdit', params: { 'id': feature.properties.id } }).href}')"><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="edit" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" class="svg-inline--fa fa-edit fa-w-18"><path fill="currentColor" d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.3 15.2-40 0-55.2zM384 346.2V448H64V128h229.8c3.2 0 6.2-1.3 8.5-3.5l40-40c7.6-7.6 2.2-20.5-8.5-20.5H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V306.2c0-10.7-12.9-16-20.5-8.5l-40 40c-2.2 2.3-3.5 5.3-3.5 8.5z" class=""></path></svg></button>`

          // layer.bindPopup(
          //   `<div class="text-center">${mssg}</div>`,
          //   { permanent: false, sticky: false, closeButton: false }
          // )
          layer.on('contextmenu', () => {
            this.selectedItems = [...new Set(this.items[feature.properties.id])].sort((a, b) => a.distance - b.distance)

            this.selectedGeofence = {
              externalId: feature.properties.externalId,
              name: feature.properties.name,
              referenceLocation: feature.properties.referenceLocation
            }
            this.$refs['modalItemsFromGeofence' + this.rnd].show()
          })
        }
      }
    },
    pointToLayer() {
      // Set entry and exit points to a circle icon
      return (feature, latlng) => {
        return circleMarker(latlng, {
          radius: 5
        })
      }
    }
  },
  watch: {
    radius(newValue, oldValue) {
      // The radius only affects the "out" layer (its circle and distance filter),
      // so we only refetch when at least one "out" layer is currently visible.
      if (this.hasVisibleOutLayer()) {
        this.fetchOrders()
      }
    },
    mapLayers: {
      handler(newVal, oldVal) {
        this.$refs.fullMap.fitMap()
      },
      deep: true
    }
  },
  mounted() {
    this.radiusSelect = this.radiusList.map(x => {
      return {
        text: this.$options.filters.distance(x * this.unitSystemMultiplier, { unitSystem: this.frontConfig.i18n.unitSystem, locale: this.frontConfig.i18n.moment }),
        value: x
      }
    })
    this.radius = this.radiusDefault
    this.fetchOrders()
  },
  beforeDestroy() {
    clearTimeout(this.fetchTimeout)
    this.fetchTimeout = null
  },
  methods: {
    async fetchOrders() {
      this.updating = true
      if (this.updateEvery) {
        clearTimeout(this.fetchTimeout)
      }
      this.markers = []
      for (let i = 0; i < this.orders.length; i++) {
        const order = this.orders[i]
        if (!this.mapLayers[i]) {
          this.mapLayers.push({})
        } else {
          this.mapLayers[i] = {}
        }
        this.$set(this.mapLayers, i, {
          data: { ...await this.fetchOrder(order) },
          visible: true
        })
      }

      this.$nextTick(() => {
        this.$refs.fullMap.fitMap()
      })
      this.updating = false
      if (this.updateEvery) {
        this.fetchTimeout = setTimeout(() => {
          this.fetchOrders()
        }, this.updateEvery * 1000)
      }
    },
    async fetchOrder(order) {
      return await this.setShippingOrderMapData(order)
    },
    async setShippingOrderMapData(order) {
      const output = {}
      this.$set(output, 'id', order.id)
      this.$set(output, 'geofences', [])
      this.$set(output, 'geopaths', [])
      this.$set(output, 'geopoints', [])
      this.$set(output, 'couriers', [])
      this.$set(output, 'items', [])

      // output.geofences = []
      // Origin
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'origin') {
        output.geofences.push({
          type: 'Feature',
          geometry: order.origin.fence,
          properties: {
            id: order.origin.id,
            checkpoint: this.$gettext('Origin'),
            externalId: order.origin.external_id,
            name: order.origin.name,
            type: order.origin.geo_type.type,
            typeName: order.origin.geo_type.name,
            referenceLocation: order.origin.reference_location,
            level: order.origin.geo_type.level
          }
        })
      }

      // Destination
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'destination') {
        output.geofences.push({
          type: 'Feature',
          geometry: order.destination.fence,
          properties: {
            id: order.destination.id,
            checkpoint: this.$gettext('Destination'),
            externalId: order.destination.external_id,
            name: order.destination.name,
            type: order.destination.geo_type.type,
            typeName: order.destination.geo_type.name,
            referenceLocation: order.destination.reference_location,
            level: order.destination.geo_type.level
          }
        })
      }

      // Origin secondary geofences
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'origin') {
        for (const geofence of order.origin.geo_fences) {
          output.geofences.push({
            type: 'Feature',
            geometry: geofence.fence,
            properties: {
              id: geofence.id,
              checkpoint: this.$gettext('Secondary'),
              externalId: geofence.external_id,
              name: geofence.name,
              type: geofence.geo_type.type,
              typeName: geofence.geo_type.name,
              referenceLocation: geofence.reference_location || null,
              level: geofence.geo_type.level
            }
          })
        }
      }

      // Destination secondary geofences
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'destination') {
        for (const geofence of order.destination.geo_fences) {
          output.geofences.push({
            type: 'Feature',
            geometry: geofence.fence,
            properties: {
              id: geofence.id,
              checkpoint: this.$gettext('Secondary'),
              externalId: geofence.external_id,
              name: geofence.name,
              type: geofence.geo_type.type,
              typeName: geofence.geo_type.name,
              referenceLocation: geofence.reference_location || null,
              level: geofence.geo_type.level
            }
          })
        }
      }

      // output.geopaths = []
      // Origin paths
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'origin') {
        for (const geofence of order.origin.geo_lines) {
          output.geopaths.push({
            type: 'Feature',
            geometry: geofence.path,
            properties: {
              id: geofence.id,
              checkpoint: this.$gettext('Path'),
              externalId: geofence.external_id,
              name: geofence.name,
              type: geofence.geo_type.type,
              typeName: geofence.geo_type.name,
              referenceLocation: geofence.reference_location || null,
              level: geofence.geo_type.level
            }
          })
        }
      }

      // Destination paths
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'destination') {
        for (const geofence of order.destination.geo_lines) {
          output.geopaths.push({
            type: 'Feature',
            geometry: geofence.path,
            properties: {
              id: geofence.id,
              checkpoint: this.$gettext('Path'),
              externalId: geofence.external_id,
              name: geofence.name,
              type: geofence.geo_type.type,
              typeName: geofence.geo_type.name,
              referenceLocation: geofence.reference_location || null,
              level: geofence.geo_type.level
            }
          })
        }
      }

      // output.geopoints = []
      // Origin points
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'origin') {
        for (const geofence of order.origin.geo_points) {
          output.geopoints.push({
            type: 'Feature',
            geometry: geofence.point,
            properties: {
              id: geofence.id,
              checkpoint: this.$gettext('Point'),
              externalId: geofence.external_id,
              name: geofence.name,
              type: geofence.geo_type.type,
              typeName: geofence.geo_type.name,
              referenceLocation: geofence.reference_location || null,
              level: geofence.geo_type.level
            }
          })
        }
      }

      // Destination points
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'destination') {
        for (const geofence of order.destination.geo_points) {
          output.geopoints.push({
            type: 'Feature',
            geometry: geofence.point,
            properties: {
              id: geofence.id,
              checkpoint: this.$gettext('Path'),
              externalId: geofence.external_id,
              name: geofence.name,
              type: geofence.geo_type.type,
              typeName: geofence.geo_type.name,
              referenceLocation: geofence.reference_location || null,
              level: geofence.geo_type.level
            }
          })
        }
      }

      for (const shipping of order.shippings) {
        // Shippings' Couriers
        if (!this.onlyCheckpoint && shipping.courier && shipping.courier.courier_available && shipping.courier.courier_available.tracking) {
          if (shipping.courier.courier_available.tracking[0]) {
            const icon = this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === shipping.courier.courier_available.vehicle_type_id).name.toLowerCase() + '-gray.svg'

            output.couriers.push(this.$refs.fullMap.transformMarker({
              id: 1000,
              lat: shipping.courier.courier_available.tracking[0].lat,
              lng: shipping.courier.courier_available.tracking[0].lng,
              icon,
              x: 25,
              y: 25,
              infoText: { id: shipping.courier.id, type: 'courier', user: shipping.courier },
              popup: true
            }))
          }
        }
        // Shippings' Items
        shipping.items = shipping.items || []
        output.items = output.items.concat(this.transformSelfItems(shipping.items, 'inShipping'))
      }

      console.log('only checkpoint', this.onlyCheckpoint)
      console.log('origin', order.origin)
      console.log('destination', order.destination)
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'origin') {
        await this.fetchItems(order.origin, order.id)
      }
      if (!this.onlyCheckpoint || this.onlyCheckpoint === 'destination') {
        await this.fetchItems(order.destination, order.id)
      }

      return output
    },
    hasVisibleOutLayer() {
      // "out" layer keys are `${orderId}out${geofenceId}`; ids are numeric, so the
      // only key segment that can contain "out" is the status.
      return Object.keys(this.fullMapMapLayers).some(
        key => key.includes('out') && this.fullMapMapLayers[key].visible
      )
    },
    onLayerToggle(order, checkpoint, status) {
      // Only the "out" layer fetches on-demand; "in" already has its data loaded.
      if (status !== 'out') {
        return
      }
      const geofence = order[checkpoint]
      // v-model already updated .visible before this @change. If it became visible,
      // we fetch the data right away instead of waiting for the next polling cycle.
      if (this.fullMapMapLayers[order.id + status + geofence.id].visible) {
        this.fetchItems(geofence, order.id)
      }
    },
    async fetchItems(geofence, orderId) {
      const items = {}
      let params = {
        extendedFences: true,
        sort: [
          {
            key: 'last_position_updated_at',
            direction: 'desc'
          }
        ],
        includes: ['item.item_tracking', 'item.location', 'item.shipping']
      }
      await GeofenceService.getItems(geofence.id, { params })
        .then(response => {
          if (!this.fullMapMapLayers[orderId + 'in' + geofence.id]) {
            this.$set(this.fullMapMapLayers, orderId + 'in' + geofence.id, {})
            this.$set(this.fullMapMapLayers[orderId + 'in' + geofence.id], 'visible', true)
          }
          this.$set(this.fullMapMapLayers[orderId + 'in' + geofence.id], 'markers', this.transformSelfItems(response.body.filter(x => x.active)))
          this.$set(this.fullMapMapLayers[orderId + 'in' + geofence.id], 'count', response.body.filter(x => x.active).length)

          items[geofence.id] = response.body.filter(x => x.active).map(x => {
            x.distance = this.getDistance({ lat: geofence.lat, lng: geofence.lng }, { lat: x.item_tracking.lat, lng: x.item_tracking.lng })
            return x
          })
        })

      // We initialize the "out" layer hidden (visible: false) so its checkbox
      // renders and the user can enable it manually. Its data is NOT requested
      // until visible === true.
      if (!this.fullMapMapLayers[orderId + 'out' + geofence.id]) {
        this.$set(this.fullMapMapLayers, orderId + 'out' + geofence.id, {})
        this.$set(this.fullMapMapLayers[orderId + 'out' + geofence.id], 'visible', false)
        this.$set(this.fullMapMapLayers[orderId + 'out' + geofence.id], 'count', 0)
      }

      // The second call (items outside the geofence, within the radius) only happens
      // when the user made the "out" layer visible; it stops happening if they hide it.
      if (this.fullMapMapLayers[orderId + 'out' + geofence.id].visible) {
        params = {
          sort: [
            {
              key: 'last_position_updated_at',
              direction: 'desc'
            }
          ],
          includes: ['item_tracking', 'location', 'shipping'],
          fields: 'id,reference_code,location_model_id,location.external_id,location.name,location.reference_location,shipping,item_tracking.lat,item_tracking.lng,last_position_updated_at,active,item_tracking.meta.direction',
          filter_groups: [
            {
              filters: [
                ['distanceTo', 'lt', { lat: geofence.lat, lng: geofence.lng, radius: this.radiusComputed }]
              ]
            }
          ]
        }
        await ItemService.getAllPages({ params })
          .then(response => {
            this.$set(this.fullMapMapLayers[orderId + 'out' + geofence.id], 'circles', [{
              center: [geofence.lat, geofence.lng],
              radius: this.radiusComputed,
              color: '#ccc',
              weight: 1,
              opacity: 1,
              fillColor: '#ccc',
              fillOpacity: 0.2,
              dashArray: '2, 2',
              dashOffset: '0'
            }])
            const inIds = new Set(items[geofence.id].map(x => x.id))
            const outItems = response.body.data.filter(x => !inIds.has(x.id) && x.active)

            this.$set(this.fullMapMapLayers[orderId + 'out' + geofence.id], 'markers', this.transformSelfItems(outItems))
            this.$set(this.fullMapMapLayers[orderId + 'out' + geofence.id], 'count', outItems.length)

            items[geofence.id] = items[geofence.id].concat(outItems.map(x => {
              x.distance = this.getDistance({ lat: geofence.lat, lng: geofence.lng }, { lat: x.item_tracking.lat, lng: x.item_tracking.lng })
              return x
            }))
          })
      }

      this.items = items
    },
    transformSelfItems(data, type) {
      return data.map((item, i) => {
        if (item.item_tracking === null || !item.item_tracking.lat) {
          return false
        }

        // Parse and reformat attributes
        if (item.attributes?.length) {
          item.parsedAttributes = {}
          for (const attribute of item.attributes) {
            item.parsedAttributes[attribute.name] = attribute.value
          }
        } else {
          item.parsedAttributes = null
        }

        let itemColor
        if (this.ecosystemModules.items.icon.colorByMinAge) {
          const daysIdle = this.elapsedTimeRaw(item.last_position_updated_at || item.item_tracking.last_position_updated_at) / 86400000
          itemColor = this.itemColorByAge(daysIdle, 'items')
        }
        let direction = 0
        if (this.ecosystemModules.items.directionableBy) {
          direction = this.getItemDirection(this.ecosystemModules.items.directionableBy.split('.').reduce((o, i) => o[i], item))
        }

        let dynamic = null
        try {
          if (this.ecosystemModules.items.dynamicableBy) {
            dynamic = this.getItemDynamic(this.ecosystemModules.items.dynamicableBy.split('.').reduce((o, i) => o[i], item))
          }
        } catch (e) {
          console.log(e)
        }

        let load = null
        try {
          if (this.ecosystemModules.items.loadableBy) {
            load = this.ecosystemModules.items.loadableBy.split('.').reduce((o, i) => o[i], item)
          }
        } catch (e) {
          console.assert(e)
        }

        if (item.item_tracking) {
          let infoWindowHtml = ''
          infoWindowHtml += this.$options.filters.cleanentityprefix(item.reference_code)
          return this.$refs.fullMap.transformMarker({
            id: (i + 100000),
            cardType: 'item',
            lat: item.item_tracking.lat,
            lng: item.item_tracking.lng,
            icon: {
              type: this.ecosystemModules.items.icon.type,
              size: this.ecosystemModules.items.icon.size,
              anchor: this.ecosystemModules.items.icon.anchor,
              popupAnchor: this.ecosystemModules.items.icon.popupAnchor,
              color: item.active || type === 'inShipping' ? (itemColor || '#000') : '#333',
              text: this.ecosystemModules.items.icon.text || this.$options.filters.cleanentityprefix(item.reference_code),
              direction,
              dynamic,
              load
            },
            x: 30,
            y: 30,
            optimized: false,
            infoText: {
              id: item.id,
              type: 'item-id',
              html: infoWindowHtml,
              color: item.active ? itemColor : '#333',
              data: item,
              isShared: item.isShared || false
            },
            popup: true
          })
        }
        return false
      }).filter(Boolean)
    },
    downloadItemsCsv(items) {
      const selectedItemsCsv = []
      let fileName = this.stripHtml(this.$gettextInterpolate(this.$gettext('%{itemsLabel} from <strong>%{geofence}</strong>'), { itemsLabel: this.ecosystemModules.items.label || this.$gettext('Items'), geofence: this.$options.filters.cleanentityprefix(this.selectedGeofence.externalId) })) + '_' + this.$options.filters.cleanentityprefix(this.selectedGeofence.name) + '_' + this.moment().format('YYYYMMDDHHmmss') + '_' + this.$options.filters.distance(this.radiusComputed, { unitSystem: this.frontConfig.i18n.unitSystem, locale: this.frontConfig.i18n.moment })
      fileName = fileName.toLowerCase().replace(/\s/g, '-') + '.csv'
      for (const item of items) {
        selectedItemsCsv.push({
          id: item.id,
          reference_code: this.$options.filters.cleanentityprefix(item.reference_code),
          last_position_updated_at: this.moment.utc(item.last_position_updated_at).tz(this.$tz).format('L LT'),
          active: item.active,
          location: item.location ? `${this.$options.filters.cleanentityprefix(item.location.external_id)} - ${this.$options.filters.cleanentityprefix(item.location.name)} (${item.location.reference_location})` : '',
          in_shipping: item.shipping ? item.shipping.id : '',
          distance: this.$options.filters.distance(item.distance, { unitSystem: this.frontConfig.i18n.unitSystem, locale: this.frontConfig.i18n.moment })
        })
      }
      this.download(this.toCsv(selectedItemsCsv), 'text/csv', fileName)
    }
  }
}
</script>
